import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBlog } from '../../context/BlogContext';
import BlogHeroSlider from '../../components/blog/BlogHeroSlider';
import BlogBlockRenderer from '../../components/blog/BlogBlockRenderer';
import { calculateReadingTime } from '../../components/blog/BlogCard';
import SEO, { articleJsonLd } from '../../components/SEO';
import '../../styles/Blog.css';

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getPostBySlug, categories, loading } = useBlog();
  const post = getPostBySlug(slug);

  // Mientras cargan los posts del backend, no mostrar "not found" (evita parpadeo).
  // Usamos los 3 puntitos parpadeantes del sitio (mismo patrón que la tienda).
  if (loading && !post) {
    return (
      <div className="blog-article" data-header-theme="light">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: '60vh' }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'var(--phys-ink, #1C2B3A)',
                animation: 'blogDotPulse 0.6s ease-in-out infinite',
                animationDelay: `${i * 150}ms`,
              }}
            />
          ))}
          <style>{`@keyframes blogDotPulse {0%,100%{opacity:0.4;transform:scale(0.8);}50%{opacity:1;transform:scale(1);}}`}</style>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-article" data-header-theme="light">
        <SEO title="Artículo no encontrado" description="Este artículo no existe o fue movido." noindex />
        <button onClick={() => navigate('/journal')} className="blog-back-btn">
          ← Back to journal
        </button>
        <div className="blog-article-header">
          <h1>Article not found</h1>
        </div>
      </div>
    );
  }

  const category = categories.find(c => c.slug === post.category);
  const sortedBlocks = [...(post.blocks || [])].sort((a, b) => a.order - b.order);
  const readingTime = calculateReadingTime(post);

  const articleLd = articleJsonLd({
    title: post.title,
    description: post.excerpt || post.title,
    image: post.coverImage,
    slug: post.slug,
    datePublished: post.publishedAt,
    author: post.author,
  });

  return (
    <div className="blog-article" data-header-theme="light">
      <SEO
        title={post.title}
        description={(post.excerpt || post.title).slice(0, 300)}
        path={`/journal/${post.slug}`}
        image={post.coverImage}
        type="article"
        jsonLd={articleLd}
      />
      {/* Back to journal */}
      <button onClick={() => navigate('/journal')} className="blog-back-btn">
        ← Back to journal
      </button>

      {/* Post header */}
      <header className={`blog-article-header${post.headerImage ? ' blog-article-header--with-image' : ''}`}>
        <div className="blog-article-header-text">
          {category && <p className="blog-article-category">{category.name}</p>}
          <h1 className="blog-article-title">{post.title}</h1>
          <span className="blog-article-meta">
            Tiempo de lectura: {readingTime}
          </span>
        </div>
        {/* Imagen a la derecha del encabezado (artículos importados de WordPress) */}
        {post.headerImage && (
          <img className="blog-article-header-image" src={post.headerImage} alt="" />
        )}
      </header>

      {/* Hero Slider */}
      {post.heroSlider?.enabled && post.heroSlider.slides.length > 0 && (
        <BlogHeroSlider data={post.heroSlider} isPageHero />
      )}

      {/* Content blocks */}
      <div className={`blog-article-content blog-article-content--${post.contentWidth || 'narrow'}`}>
        {sortedBlocks.map(block => (
          <BlogBlockRenderer key={block.id} block={block} />
        ))}
      </div>

      {/* "Continúa explorando" ahora es un bloque editable (BlogRelatedPostsBlock)
          que se agrega desde el editor; se acabó la sección automática duplicada. */}
    </div>
  );
}
